'use client';

import { AppImageClient } from '@components/appImage';
import { Sanitizer } from '@components/sanitizer';
import { Typography } from '@components/typography';
import { classNames } from '@lib/classNames/classNames';
import { cn } from '@lib/utils/cn';
import { isNumber, isUndefined } from 'lodash';
import Link from 'next/link';

import { SmallCardProps } from '../types';
import { Card } from './Card';

import { useIsMobile, useUtmHref } from '@/shared/hooks';
import { IconArrowRightCorner24 } from '@/shared/icons/24/IconArrowRightCorner24';

export const SmallCard = ({
  title,
  titleVariant,
  titleId,
  description,
  descriptionId,
  href,
  imageSrc,
  imageData,
  alt,
  cardHeight = 236,
  cardHeightMobile,
  variant = 'secondary',
  className,
  descriptionTypographVariant = 'text-m',
  contentGap,
  rounded,
}: SmallCardProps) => {
  const isMobile = useIsMobile();
  const hrefWithUtm = useUtmHref(href);

  const content = (
    <Card
      variant={variant}
      rounded={rounded}
      className={cn(
        'flex size-full flex-col p-7 pt-6 max-sm:px-5 max-sm:py-6',
        isUndefined(contentGap) ? 'justify-between' : 'justify-start',
        href && 'from-white/20 to-white/5 hover:inset-0 hover:bg-linear-to-b',
        className,
      )}
      style={
        isNumber(contentGap)
          ? {
              rowGap: contentGap,
            }
          : undefined
      }
    >
      <div
        className={classNames({
          cls: 'flex justify-between',
        })}
      >
        {imageData ? (
          <AppImageClient {...imageData} width={96} height={96} alt={alt} />
        ) : (
          imageSrc && (
            <img
              src={imageSrc}
              width={96}
              height={96}
              alt={alt}
              decoding="async"
              loading="lazy"
            />
          )
        )}
        {href && (
          <div className="bg-custom-surface-contrast-low group-hover:bg-surface-base-inverse-primary mt-[9px] flex h-12 w-12 items-center justify-center rounded-[12px] transition-colors duration-300">
            <IconArrowRightCorner24 className="transition-transform duration-300 group-hover:rotate-45" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <Typography variant={titleVariant ?? 'heading-s'}>
          {typeof title === 'string' ? (
            <Sanitizer id={titleId} as="span" htmlContent={title} />
          ) : (
            title
          )}
        </Typography>

        <Typography variant={descriptionTypographVariant}>
          {typeof description === 'string' ? (
            <Sanitizer id={descriptionId} as="span" htmlContent={description} />
          ) : (
            description
          )}
        </Typography>
      </div>
    </Card>
  );

  const commonClassName = cn(
    'group block size-full',
    href &&
      'cursor-pointer transition-transform duration-300 hover:scale-[1.02]',
  );

  const wrapperStyles = { minHeight: isMobile ? cardHeightMobile : cardHeight };

  if (hrefWithUtm) {
    return (
      <Link
        href={hrefWithUtm}
        target="_blank"
        className={commonClassName}
        style={wrapperStyles}
      >
        {content}
      </Link>
    );
  }

  return (
    <div className={commonClassName} style={wrapperStyles}>
      {content}
    </div>
  );
};
